import { alpha, Theme } from "@mui/material/styles";
import type { Components } from "@mui/material/styles";
import { control, motion, radius, shadow, transition, type } from "./tokens";
import { scrollbarBaseline } from "./scrollbars";
import { buttonRole } from "./buttonRoles";

/**
 * Global MUI component layer.
 *
 * This is the spine of the GT Brokers redesign. The portal is ~100 routes and
 * 266 files built over several years with per-page styling; restyling them one
 * at a time would guarantee that some screens stayed old. Instead every screen
 * inherits its geometry, density, colour and motion from here, because every
 * screen is built out of these MUI components.
 *
 * Rules this layer follows:
 *  - shape and behaviour only — no component is given content or logic;
 *  - nothing is sized so differently that an existing layout reflows badly;
 *  - every colour comes from the palette, so Setting → Theme Color still wins;
 *  - focus states are always visible, because they were mostly absent before.
 */
export const buildComponents = (theme: Theme): Components<Theme> => {
  const isLight = theme.palette.mode === "light";
  const line = theme.palette.divider;
  const strong = theme.palette.text.secondary; // see the role note in palette.ts
  const muted = theme.palette.text.primary;
  const brand = theme.palette.primary.main;
  const surface = theme.palette.cardAdd.bgColor;
  const sunken = theme.palette.cardAdd.secBackColor;

  /** Consistent keyboard ring. Used by every focusable surface below. */
  const focusRing = {
    outline: `2px solid ${alpha(brand, 0.55)}`,
    outlineOffset: 2,
  };

  /* The two button roles this layer needs by name. Everything else resolves
     through `MuiButton`'s own colour props. */
  const neutral = buttonRole(theme, "neutral");
  const primarySolid = buttonRole(theme, "primary", true);

  const overlaySurface = {
    backgroundColor: surface,
    backgroundImage: "none",
    border: `1px solid ${line}`,
    borderRadius: radius.lg,
    boxShadow: isLight ? shadow.overlay : shadow.overlayDark,
  };

  return {
    MuiCssBaseline: {
      styleOverrides: {
        ...scrollbarBaseline(theme),
        body: {
          ...(scrollbarBaseline(theme).body as object),
          backgroundColor: theme.palette.background.default,
          color: strong,
          fontSize: `${type.body}px`,
          WebkitFontSmoothing: "antialiased",
          /* Ambient depth for the whole product.
             The page was one flat fill, which is most of why the software read
             as a template regardless of what was drawn on top of it. Two very
             soft radials — brand at the top-left, cyan at the top-right — give
             the page a light source without becoming a visible gradient. Both
             are `fixed`, so the wash does not slide or band as the content
             scrolls, and they sit at alphas low enough that a white card still
             reads as white against them.
             CSS only: no image, no extra request, no repaint cost. */
          backgroundImage: isLight
            ? `radial-gradient(1100px 520px at 8% -140px, ${alpha(brand, 0.1)} 0%, transparent 70%),
               radial-gradient(900px 460px at 96% -90px, ${alpha(theme.palette.info.main, 0.09)} 0%, transparent 72%)`
            : `radial-gradient(1100px 520px at 8% -140px, ${alpha(brand, 0.16)} 0%, transparent 70%),
               radial-gradient(900px 460px at 96% -90px, ${alpha(theme.palette.info.main, 0.1)} 0%, transparent 72%)`,
          backgroundRepeat: "no-repeat",
          backgroundAttachment: "fixed",
        },
        /* ------------------------------------------------------- FullCalendar
           The Meetings, Schedule and Reschedule calendars render their own
           toolbar — today, prev/next and the month/week/day switch are
           FullCalendar's DOM, not MUI's, so nothing above reaches them and they
           were still drawing the library's stock `#2C3E50` dark navy. That is
           the "dark grey active buttons" on the Meetings screen.

           Styled here rather than per page, so every calendar in the product
           picks it up from one place. FullCalendar exposes its button
           appearance as CSS custom properties, so this sets those instead of
           fighting its selectors with `!important`, and the values come from
           the palette like everything else.

           Appearance only — no toolbar is added, removed or re-ordered, and no
           view or callback is touched. */
        ".fc": {
          /* Resting: the neutral role — a real surface with a border, not a
             grey block. */
          "--fc-button-bg-color": neutral.background,
          "--fc-button-border-color": neutral.border,
          "--fc-button-text-color": neutral.foreground,
          "--fc-button-hover-bg-color": neutral.hoverBackground,
          "--fc-button-hover-border-color": neutral.hoverBorder,
          /* Pressed / selected view: the brand primary, so exactly one of
             month | week | day reads as chosen. */
          "--fc-button-active-bg-color": primarySolid.background,
          "--fc-button-active-border-color": primarySolid.border,
        },
        ".fc .fc-button": {
          borderRadius: radius.md,
          textTransform: "none",
          fontFamily: "inherit",
          fontSize: `${type.body}px`,
          fontWeight: 600,
          minHeight: control.sm,
          paddingLeft: 14,
          paddingRight: 14,
          boxShadow: "none",
          transition: transition(
            ["background-color", "border-color", "color", "box-shadow"],
            motion.fast
          ),
        },
        ".fc .fc-button:focus": { boxShadow: "none" },
        ".fc .fc-button:focus-visible": focusRing,
        /* Selection is not carried by colour alone: the chosen view also gains
           the only filled surface in the group. */
        ".fc .fc-button-primary:not(:disabled).fc-button-active, .fc .fc-button-primary:not(:disabled):active":
          {
            color: primarySolid.foreground,
            boxShadow: "none",
          },
        ".fc .fc-button-primary:not(:disabled).fc-button-active:hover": {
          backgroundColor: primarySolid.hoverBackground,
          borderColor: primarySolid.hoverBorder,
        },
        ".fc .fc-button-primary:not(:disabled):hover": {
          color: neutral.hoverForeground,
        },
        /* Disabled stays muted rather than dark. `action.disabledBackground` is
           already an rgba value, so passing it through `alpha()` would replace
           its transparency and paint a near-black block — the sunken surface is
           the correct "unavailable" reading. */
        ".fc .fc-button-primary:disabled": {
          backgroundColor: isLight ? sunken : alpha(theme.palette.common.white, 0.04),
          borderColor: alpha(line, 0.7),
          color: theme.palette.text.disabled,
          opacity: 1,
        },
        /* The prev/next pair reads as one compact control. */
        ".fc .fc-button-group > .fc-button": { minWidth: 38 },
        ".fc .fc-toolbar-title": {
          fontSize: `${type.sectionTitle}px`,
          fontWeight: 700,
          color: strong,
        },

        /* One global reduced-motion escape hatch. Individual components no
           longer each need to remember it. */
        "@media (prefers-reduced-motion: reduce)": {
          "*, *::before, *::after": {
            animationDuration: "0.01ms !important",
            animationIterationCount: "1 !important",
            transitionDuration: "0.01ms !important",
            scrollBehavior: "auto !important",
          },
        },
      },
    },

    /* ---------------------------------------------------------------- buttons */
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          textTransform: "none",
          fontWeight: 600,
          minHeight: control.md,
          paddingLeft: 16,
          paddingRight: 16,
          transition: transition(
            ["background-color", "border-color", "color", "box-shadow"],
            motion.fast
          ),
          "&:focus-visible": focusRing,
        },
        sizeSmall: { minHeight: control.sm, fontSize: 12.5, paddingLeft: 12, paddingRight: 12 },
        sizeLarge: { minHeight: control.lg, fontSize: 14.5, paddingLeft: 22, paddingRight: 22 },
        contained: {
          boxShadow: "none",
          "&:hover": { boxShadow: "none", backgroundColor: theme.palette.primary.dark },
        },
        /* The secondary button: a real surface with a border, not a ghost.
           Outlined buttons across the app were reading as disabled. */
        outlined: {
          borderColor: line,
          backgroundColor: isLight ? surface : alpha("#ffffff", 0.02),
          color: strong,
          "&:hover": {
            borderColor: alpha(brand, 0.45),
            backgroundColor: alpha(brand, isLight ? 0.05 : 0.1),
            color: brand,
          },
        },
        text: {
          "&:hover": { backgroundColor: alpha(brand, isLight ? 0.06 : 0.12) },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          transition: transition(["background-color", "color"], motion.fast),
          "&:focus-visible": focusRing,
        },
      },
    },

    /* The segmented control — Documentation's Guide/Technical switch and any
       other `ToggleButtonGroup`. Only shape was set here before, so a selected
       segment fell back to MUI's default: text-primary at 9%, a flat grey that
       did not read as chosen. It now takes the same treatment as the calendar's
       month/week/day switch, so both look like one control. */
    MuiToggleButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          textTransform: "none",
          fontWeight: 600,
          borderColor: neutral.border,
          backgroundColor: neutral.background,
          color: neutral.foreground,
          "&:hover": {
            backgroundColor: neutral.hoverBackground,
            color: neutral.hoverForeground,
          },
          "&.Mui-selected": {
            backgroundColor: primarySolid.background,
            color: primarySolid.foreground,
            borderColor: primarySolid.border,
            "&:hover": {
              backgroundColor: primarySolid.hoverBackground,
              color: primarySolid.foreground,
            },
          },
          "&.Mui-disabled": {
            backgroundColor: isLight ? sunken : alpha(theme.palette.common.white, 0.04),
            color: theme.palette.text.disabled,
          },
        },
      },
    },

    /* ----------------------------------------------------------------- inputs */
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          backgroundColor: isLight ? surface : alpha("#ffffff", 0.02),
          fontSize: `${type.body}px`,
          transition: transition(["border-color", "box-shadow", "background-color"], motion.fast),
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.palette.cardAdd.borderColor,
            transition: transition(["border-color"], motion.fast),
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(brand, 0.4),
          },
          /* A 1px focus border plus a soft ring, rather than MUI's 2px jump —
             the thicker border shifted text by a pixel on focus. */
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderWidth: 1,
            borderColor: brand,
          },
          "&.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(brand, 0.14)}`,
          },
          "&.Mui-error.Mui-focused": {
            boxShadow: `0 0 0 3px ${alpha(theme.palette.error.main, 0.16)}`,
          },
          "&.Mui-disabled": {
            backgroundColor: isLight ? sunken : alpha("#ffffff", 0.02),
          },
        },
        input: {
          paddingTop: 10.5,
          paddingBottom: 10.5,
          color: strong,
          "&::placeholder": { color: muted, opacity: 1 },
        },
        inputSizeSmall: { paddingTop: 8, paddingBottom: 8 },
      },
    },

    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: `${type.body}px`,
          color: muted,
          "&.Mui-focused": { color: brand },
        },
        /* The required marker was the same weight as the label. */
        asterisk: { color: theme.palette.error.main, fontWeight: 700 },
      },
    },

    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: `${type.body}px`,
          fontWeight: 500,
          color: muted,
          "&.Mui-focused": { color: brand },
        },
        asterisk: { color: theme.palette.error.main, fontWeight: 700 },
      },
    },

    MuiFormHelperText: {
      styleOverrides: {
        root: {
          fontSize: `${type.meta}px`,
          marginLeft: 2,
          marginTop: 5,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        select: { fontSize: `${type.body}px`, color: strong },
      },
    },

    MuiAutocomplete: {
      styleOverrides: {
        paper: overlaySurface,
        option: {
          fontSize: `${type.body}px`,
          borderRadius: radius.sm,
          margin: "2px 6px",
          minHeight: 36,
          '&[aria-selected="true"]': {
            backgroundColor: alpha(brand, isLight ? 0.09 : 0.16),
          },
        },
        inputRoot: { paddingTop: 3, paddingBottom: 3 },
      },
    },

    MuiCheckbox: {
      styleOverrides: {
        root: {
          borderRadius: radius.xs,
          color: alpha(strong, 0.35),
          "&:focus-visible": focusRing,
        },
      },
    },

    MuiRadio: {
      styleOverrides: {
        root: { color: alpha(strong, 0.35), "&:focus-visible": focusRing },
      },
    },

    MuiSwitch: {
      styleOverrides: {
        switchBase: { "&:focus-visible": focusRing },
      },
    },

    /* --------------------------------------------------------------- surfaces */
    MuiPaper: {
      styleOverrides: {
        /* MUI paints an alpha "elevation" gradient over Paper in dark mode,
           which made every stacked surface a slightly different colour. */
        root: { backgroundImage: "none" },
      },
    },

    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: radius.xl,
          border: `1px solid ${line}`,
          backgroundColor: surface,
          boxShadow: isLight ? shadow.card : shadow.cardDark,
          backgroundImage: "none",
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: { padding: 22, "&:last-child": { paddingBottom: 22 } },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: { padding: "18px 22px 0" },
        title: { fontSize: `${type.cardTitle}px`, fontWeight: 660, color: strong },
        subheader: { fontSize: `${type.meta}px`, color: muted },
      },
    },

    MuiAccordion: {
      defaultProps: { elevation: 0, disableGutters: true },
      styleOverrides: {
        root: {
          border: `1px solid ${line}`,
          borderRadius: `${radius.lg}px !important`,
          backgroundColor: surface,
          marginBottom: 10,
          "&::before": { display: "none" },
        },
      },
    },

    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          minHeight: 52,
          padding: "0 18px",
          borderRadius: radius.lg,
          "&.Mui-expanded": { minHeight: 52 },
          "&:focus-visible": focusRing,
        },
        content: { margin: "12px 0", "&.Mui-expanded": { margin: "12px 0" } },
      },
    },

    MuiDivider: {
      styleOverrides: { root: { borderColor: line } },
    },

    /* ----------------------------------------------------------------- tables */
    MuiTableContainer: {
      styleOverrides: { root: { borderRadius: radius.xl } },
    },

    MuiTable: {
      styleOverrides: { root: { borderCollapse: "separate", borderSpacing: 0 } },
    },

    MuiTableHead: {
      styleOverrides: {
        root: {
          /* The header band used to be `sunken` — a near-white grey, which is
             the single strongest "generic admin table" signal in the product
             and the thing that made every list read the same. It now carries a
             low-alpha two-stop wash of the brand colour plus an accent
             underline, which is enough to make the header a deliberate band
             without tinting the data below it.
             Screens that want their own module accent override this locally
             via `premiumTableSx`; this is the global default for the ~40 tables
             that do not. */
          background: isLight
            ? `linear-gradient(180deg, ${alpha(brand, 0.09)} 0%, ${alpha(brand, 0.05)} 100%)`
            : `linear-gradient(180deg, ${alpha("#ffffff", 0.06)} 0%, ${alpha("#ffffff", 0.03)} 100%)`,
          "& .MuiTableCell-root": {
            /* The one place uppercase earns its keep: short column labels read
               faster as a distinct band above the data. */
            textTransform: "uppercase",
            fontSize: `${type.tableHeader}px`,
            fontWeight: 700,
            letterSpacing: "0.06em",
            color: muted,
            whiteSpace: "nowrap",
            paddingTop: 12,
            paddingBottom: 12,
            borderBottom: `1px solid ${line}`,
          },
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        root: {
          /* Enterprise density: tighter than MUI's 16px default, still legible.
             Rows land at roughly 52–56px with a single line of content. */
          padding: "12px 16px",
          fontSize: `${type.body}px`,
          color: strong,
          borderBottom: `1px solid ${alpha(line, isLight ? 0.7 : 0.5)}`,
        },
        head: { lineHeight: 1.4 },
        sizeSmall: { padding: "8px 12px" },
      },
    },

    MuiTableRow: {
      styleOverrides: {
        root: {
          transition: transition(["background-color"], motion.fast),
          "&:last-child .MuiTableCell-root": { borderBottom: "none" },
          "&.Mui-selected": {
            backgroundColor: alpha(brand, isLight ? 0.07 : 0.14),
            "&:hover": { backgroundColor: alpha(brand, isLight ? 0.1 : 0.18) },
          },
        },
        hover: {
          "&:hover": { backgroundColor: theme.palette.cardAdd.tablehover },
        },
      },
    },

    MuiTableSortLabel: {
      styleOverrides: {
        root: { "&:focus-visible": focusRing, "&.Mui-active": { color: brand } },
      },
    },

    /* ---------------------------------------------------------------- overlays */
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: radius.xl,
          border: `1px solid ${line}`,
          backgroundColor: surface,
          backgroundImage: "none",
          boxShadow: isLight ? shadow.dialog : shadow.dialogDark,
        },
      },
    },

    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: 17,
          fontWeight: 680,
          letterSpacing: "-0.01em",
          color: strong,
          padding: "20px 24px 4px",
        },
      },
    },

    MuiDialogContent: {
      styleOverrides: { root: { padding: "12px 24px" } },
    },

    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: "16px 24px 20px",
          gap: 10,
          "& > :not(style) ~ :not(style)": { marginLeft: 0 },
        },
      },
    },

    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(isLight ? "#0B1B2E" : "#000000", 0.45),
          backdropFilter: "blur(2px)",
        },
      },
    },

    MuiMenu: {
      styleOverrides: {
        paper: overlaySurface,
        list: { padding: 6 },
      },
    },

    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontSize: `${type.body}px`,
          minHeight: 38,
          color: strong,
          transition: transition(["background-color", "color"], motion.fast),
          "&:hover": { backgroundColor: alpha(brand, isLight ? 0.06 : 0.12) },
          "&.Mui-selected": {
            backgroundColor: alpha(brand, isLight ? 0.1 : 0.18),
            "&:hover": { backgroundColor: alpha(brand, isLight ? 0.13 : 0.22) },
          },
        },
      },
    },

    MuiPopover: {
      styleOverrides: { paper: overlaySurface },
    },

    MuiTooltip: {
      defaultProps: { arrow: true, enterDelay: 350 },
      styleOverrides: {
        tooltip: {
          backgroundColor: isLight ? "#14212F" : "#26344A",
          color: "#F3F6FB",
          fontSize: 12,
          fontWeight: 500,
          padding: "7px 10px",
          borderRadius: radius.sm,
          boxShadow: shadow.overlay,
        },
        arrow: { color: isLight ? "#14212F" : "#26344A" },
      },
    },

    /* -------------------------------------------------------------- indicators */
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: radius.pill,
          fontWeight: 600,
          fontSize: 12,
          height: 26,
        },
        label: { paddingLeft: 10, paddingRight: 10 },
        outlined: { borderColor: line },
      },
    },

    MuiBadge: {
      styleOverrides: {
        badge: { fontSize: 10.5, fontWeight: 700, minWidth: 18, height: 18 },
      },
    },

    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          fontSize: `${type.body}px`,
          border: `1px solid ${line}`,
          alignItems: "center",
        },
        standardSuccess: {
          backgroundColor: alpha(theme.palette.success.main, isLight ? 0.08 : 0.16),
          borderColor: alpha(theme.palette.success.main, 0.3),
          color: isLight ? theme.palette.success.main : "#9AE6C4",
        },
        standardWarning: {
          backgroundColor: alpha(theme.palette.warning.main, isLight ? 0.1 : 0.16),
          borderColor: alpha(theme.palette.warning.main, 0.3),
          color: isLight ? theme.palette.warning.main : "#F0CB8B",
        },
        standardError: {
          backgroundColor: alpha(theme.palette.error.main, isLight ? 0.08 : 0.16),
          borderColor: alpha(theme.palette.error.main, 0.3),
          color: isLight ? theme.palette.error.main : "#F6B0B0",
        },
        standardInfo: {
          backgroundColor: alpha(brand, isLight ? 0.07 : 0.14),
          borderColor: alpha(brand, 0.3),
          color: isLight ? brand : "#A8DCF3",
        },
      },
    },

    MuiSkeleton: {
      defaultProps: { animation: "wave" },
      styleOverrides: {
        root: {
          backgroundColor: alpha(strong, isLight ? 0.07 : 0.12),
          borderRadius: radius.sm,
        },
      },
    },

    MuiLinearProgress: {
      styleOverrides: {
        root: { height: 5, borderRadius: radius.pill, backgroundColor: alpha(brand, 0.12) },
        bar: { borderRadius: radius.pill },
      },
    },

    /* ------------------------------------------------------------- navigation */
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 44, borderBottom: `1px solid ${line}` },
        indicator: { height: 2.5, borderRadius: "3px 3px 0 0", backgroundColor: brand },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontSize: `${type.body}px`,
          fontWeight: 600,
          minHeight: 44,
          padding: "0 16px",
          color: muted,
          transition: transition(["color", "background-color"], motion.fast),
          "&:hover": { color: strong },
          "&.Mui-selected": { color: brand },
          "&:focus-visible": focusRing,
        },
      },
    },

    MuiBreadcrumbs: {
      styleOverrides: {
        root: { fontSize: `${type.meta}px` },
        separator: { marginLeft: 6, marginRight: 6, color: alpha(muted, 0.6) },
        li: { display: "flex", alignItems: "center" },
      },
    },

    MuiLink: {
      defaultProps: { underline: "hover" },
      styleOverrides: {
        root: {
          color: brand,
          fontWeight: 500,
          textDecorationColor: alpha(brand, 0.35),
          "&:focus-visible": focusRing,
        },
      },
    },

    MuiPaginationItem: {
      styleOverrides: {
        root: {
          borderRadius: radius.sm,
          fontSize: 13,
          fontWeight: 600,
          minWidth: control.sm,
          height: control.sm,
          color: muted,
          border: `1px solid transparent`,
          transition: transition(["background-color", "border-color", "color"], motion.fast),
          "&:hover": { backgroundColor: alpha(brand, isLight ? 0.07 : 0.12), color: brand },
          "&.Mui-selected": {
            backgroundColor: brand,
            borderColor: brand,
            color: theme.palette.primary.contrastText,
            "&:hover": { backgroundColor: theme.palette.primary.dark },
          },
          "&:focus-visible": focusRing,
        },
      },
    },

    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
          transition: transition(["background-color", "color"], motion.fast),
          "&:focus-visible": focusRing,
        },
      },
    },

    MuiListItemIcon: {
      styleOverrides: { root: { minWidth: 36, color: "inherit" } },
    },

    MuiAppBar: {
      defaultProps: { color: "primary", elevation: 0 },
      styleOverrides: { root: { backgroundImage: "none" } },
    },
  };
};
